import React, { useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CheckCircle, Circle, AlertCircle, Loader2, Sparkles, ArrowLeft, Download, RotateCw, Play, Home, LayoutGrid, Save } from 'lucide-react';
import { Storyboard } from '../Storyboard';
import { generateStoryboard, mapEntitiesToShot } from '../../services/geminiService';
import { generatePreProduction } from '../../services/aiService';
import { startCloudRender, pollRenderUntilComplete, saveProjectToBackend } from '../../services/backendService';

// Stage definitions
const STAGES = [
    { id: 'storyboard', label: 'Storyboard generation' },
    { id: 'preprod',    label: 'Pre-production prep'  },
    { id: 'images',     label: 'Frame images'         },
    { id: 'videos',     label: 'Video shots'          },
    { id: 'studio',     label: 'Build timeline'       },
    { id: 'render',     label: 'Final render'         },
];

const MAX_RETRIES = 3;

// Run an async stage with up to MAX_RETRIES attempts.
// Returns { success, lastError }
const runWithRetry = async (label, fn, onAttempt) => {
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            if (onAttempt) onAttempt(attempt);
            await fn(attempt);
            return { success: true, lastError: null };
        } catch (err) {
            console.error(`[AutoPilot] ${label} attempt ${attempt} failed:`, err);
            lastError = err;
            if (attempt < MAX_RETRIES) {
                await new Promise(r => setTimeout(r, 2000 * attempt));
            }
        }
    }
    return { success: false, lastError };
};

// --- Helper: build a scene's shots[] from a storyboard generation result ---
const buildScenesFromStoryboardResult = (sbResult, characters) => {
    const sceneArr = Array.isArray(sbResult?.scenes || sbResult) ? (sbResult?.scenes || sbResult) : [];
    return sceneArr.map((sData, sIdx) => ({
        id: `scene-${sIdx}-${Date.now()}`,
        shots: (sData.shots || []).map((shData, shIdx) => {
            const { characterIds, elementIds } = mapEntitiesToShot(shData, characters);
            const rawDialogues = Array.isArray(shData.dialogues) ? shData.dialogues
                : Array.isArray(shData.dialogue) ? shData.dialogue : [];
            return {
                id: `shot-${sIdx}-${shIdx}-${Date.now()}`,
                description: shData.description,
                startFramePrompt: shData.startFramePrompt,
                endFramePrompt: shData.endFramePrompt,
                narration: shData.narration,
                characterIds,
                elementIds,
                dialogues: rawDialogues.map((d, dIdx) => {
                    const cn = (d.characterName || d.character_name || d.character || '').toLowerCase();
                    const matched = characters.find(c => {
                        const cName = (c.name || '').toLowerCase();
                        return cName && (cName === cn || cName.includes(cn) || cn.includes(cName.split(/\s+/)[0]));
                    });
                    return {
                        characterId: matched?.id,
                        characterName: d.characterName || d.character_name || d.character || '',
                        text: d.text || d.dialogue || '',
                        order: d.order ?? dIdx,
                    };
                }).filter(d => d.characterId && d.text),
            };
        }),
    }));
};

export const AutoPilot = ({
    initialData,
    onProjectUpdate,
    onAutoSave,
    onBack,
    onTrackCreditsUsed,
    onRefreshCredits,
    onSaveMedia,
    onDeductCredits,
    createTimelineFromScenes,
    onComplete,
    onBackToDashboard,
}) => {
    const project = initialData;
    const storyboardRef = useRef(null);

    const [stageIndex, setStageIndex] = useState(0);            // current stage being worked on
    const [stageStatus, setStageStatus] = useState({});          // { stageId: 'pending'|'running'|'done'|'error' }
    const [stageMessage, setStageMessage] = useState({});        // { stageId: string }
    const [stageRetryCount, setStageRetryCount] = useState({}); // { stageId: number }
    const [fatalError, setFatalError] = useState(null);          // { stageId, message } when all retries exhausted
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [showPlayer, setShowPlayer] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Force a real download (blob → object URL → synthetic <a> click). Required
    // because the rendered video lives on a cross-origin GCS URL, and browsers
    // ignore the `download` attribute on cross-origin <a href> — they navigate
    // to it instead, opening a new tab.
    const handleDownload = async () => {
        if (!downloadUrl || downloading) return;
        setDownloading(true);
        try {
            const res = await fetch(downloadUrl, { mode: 'cors' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const filename = (project?.name ? project.name.replace(/[^\w.-]+/g, '_') : 'video') + '.mp4';
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Revoke after a tick so Safari/iOS finishes the download.
            setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
        } catch (e) {
            console.error('[AutoPilot] Download failed, falling back to opening the file:', e);
            // GCS bucket might be missing CORS headers — fall back to opening
            // in a new tab so the user can right-click → Save As.
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        } finally {
            setDownloading(false);
        }
    };

    // Always operate on the latest project state. We mutate via projectRef
    // so the long-running pipeline can compose updates without React closures
    // going stale.
    const projectRef = useRef(project);
    useEffect(() => { projectRef.current = project; }, [project]);

    const updateProject = useCallback((patch) => {
        projectRef.current = { ...projectRef.current, ...patch, lastModified: Date.now() };
        if (onProjectUpdate) onProjectUpdate(projectRef.current);
        return projectRef.current;
    }, [onProjectUpdate]);

    const persist = useCallback(async (reason) => {
        try {
            if (onAutoSave) await onAutoSave({ reason: `autopilot-${reason}`, projectOverride: projectRef.current });
        } catch (e) {
            console.warn('[AutoPilot] Autosave failed:', e?.message || e);
        }
    }, [onAutoSave]);

    // ------------------------------------------------------------------
    // RENDERED-VIDEO ASSET PERSISTENCE
    // ------------------------------------------------------------------
    // Push the rendered mp4 URL onto the project's `assets[]` array so that:
    //   1. It survives a project reload (the Studio editor reads from assets)
    //   2. It shows up in the media bin alongside other clips
    //   3. The user can open the project later and pick up exactly where they
    //      left off — including the final mp4.
    // Idempotent: if the same URL is already in assets, no-op.
    const attachRenderedVideoToAssets = useCallback((videoUrl) => {
        if (!videoUrl) return false;
        const p = projectRef.current;
        const existingAssets = Array.isArray(p?.assets) ? p.assets : [];
        const already = existingAssets.some(a => a?.data === videoUrl || a?.url === videoUrl);
        if (already) return false;
        const newAsset = {
            id: `asset-video-${uuidv4()}`,
            type: 'video',
            name: `Rendered Video — ${new Date().toLocaleString()}.mp4`,
            data: videoUrl,
        };
        updateProject({ assets: [...existingAssets, newAsset] });
        return true;
    }, [updateProject]);

    // Manual "Save video" button handler — idempotent. Used when the user
    // wants to explicitly re-save (e.g. after the auto-attach failed silently
    // due to a transient backend error).
    const [savingVideoState, setSavingVideoState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const handleSaveVideoToProject = useCallback(async () => {
        if (!downloadUrl) return;
        setSavingVideoState('saving');
        try {
            attachRenderedVideoToAssets(downloadUrl);
            await persist('save-video-manual');
            setSavingVideoState('saved');
            // Reset the success label after a few seconds so the user can
            // trigger it again (e.g. after editing the project).
            setTimeout(() => setSavingVideoState(s => (s === 'saved' ? 'idle' : s)), 4000);
        } catch (e) {
            console.error('[AutoPilot] Manual save-video failed:', e);
            setSavingVideoState('error');
            setTimeout(() => setSavingVideoState('idle'), 4000);
        }
    }, [downloadUrl, attachRenderedVideoToAssets, persist]);

    const setStatus = (id, status, message) => {
        setStageStatus(prev => ({ ...prev, [id]: status }));
        if (message !== undefined) setStageMessage(prev => ({ ...prev, [id]: message }));
    };
    const setRetry = (id, n) => setStageRetryCount(prev => ({ ...prev, [id]: n }));

    // ------------------------------------------------------------------
    // STAGE IMPLEMENTATIONS
    // ------------------------------------------------------------------

    const stageStoryboard = async () => {
        const p = projectRef.current;
        if (Array.isArray(p.scenes) && p.scenes.length > 0 && p.scenes.some(s => s.shots?.length > 0)) {
            setStatus('storyboard', 'done', `Reusing ${p.scenes.length} existing scenes`);
            return;
        }
        if (!(p.script || '').trim()) throw new Error('No script available to generate storyboard from.');
        const sbResult = await generateStoryboard(p, () => {});
        if (sbResult?.credits_used && onTrackCreditsUsed) onTrackCreditsUsed(sbResult.credits_used);
        const scenes = buildScenesFromStoryboardResult(sbResult, p.characters || []);
        if (!scenes.length) throw new Error('Storyboard returned no scenes.');
        updateProject({ scenes });
        await persist('storyboard');
        setStatus('storyboard', 'done', `${scenes.length} scenes ready`);
    };

    const stagePreProd = async () => {
        const p = projectRef.current;
        const scenes = p.scenes || [];
        if (!scenes.length) throw new Error('No scenes available for pre-production.');
        let processed = 0;
        // Process all scenes in parallel; tolerate per-scene failures.
        const results = await Promise.allSettled(scenes.map(async (scene) => {
            const r = await generatePreProduction(scene, p.script, p.characters || [], [], { aspectRatio: p.aspectRatio });
            return { sceneId: scene.id, result: r };
        }));
        const updatedScenes = scenes.map(s => {
            const match = results.find(r => r.status === 'fulfilled' && r.value.sceneId === s.id);
            if (!match) return s;
            const enrichment = match.value.result || {};
            // Merge any returned shot enrichments back onto the scene's shots
            // by index (the existing pattern across the codebase).
            const shotEnrichments = enrichment.shots || enrichment.shot_enrichments || [];
            const newShots = (s.shots || []).map((sh, i) => ({ ...sh, ...(shotEnrichments[i] || {}) }));
            processed++;
            return { ...s, shots: newShots };
        });
        const failures = results.filter(r => r.status === 'rejected').length;
        if (failures > 0 && processed === 0) throw new Error(`Pre-production failed for all ${failures} scenes.`);
        updateProject({ scenes: updatedScenes });
        await persist('preprod');
        setStatus('preprod', 'done', `${processed}/${scenes.length} scenes prepped${failures ? ` (${failures} failed but continuing)` : ''}`);
    };

    const stageImages = async () => {
        if (!storyboardRef.current?.runAllImages) {
            throw new Error('Storyboard component not mounted.');
        }
        await storyboardRef.current.runAllImages();
        // After completion read the latest scenes back from the storyboard
        // (Storyboard maintains its own scene state).
        const scenes = storyboardRef.current.getScenes?.() || projectRef.current.scenes;
        updateProject({ scenes });
        await persist('frame-images');
        const totalShots = scenes.reduce((n, s) => n + (s.shots?.length || 0), 0);
        // Panel-based projects: image lives at `shot.panels[i].image`, not at
        // `shot.startFrameImage` / `shot.endFrameImage`. A shot is considered
        // FULLY READY only when EVERY one of its panels has an image —
        // otherwise the video-shots stage would try to render videos for
        // half-filled shots and produce broken output (or fail silently).
        // Legacy start/end-frame projects: a shot is ready when it has
        // either a start frame OR an end frame.
        const isPanelBased = !!projectRef.current.usesPanelImages;
        const shotHasAllImages = (sh) => {
            if (isPanelBased) {
                const panels = Array.isArray(sh.panels) ? sh.panels : [];
                if (panels.length === 0) return false;
                // EVERY panel must have an image — not just one.
                return panels.every(p => !!p?.image);
            }
            return !!(sh.startFrameImage || sh.endFrameImage);
        };
        const withImages = scenes.reduce(
            (n, s) => n + (s.shots || []).filter(shotHasAllImages).length,
            0
        );
        const label = isPanelBased ? 'panel images' : 'frame images';

        // ENFORCE COMPLETENESS: if any shot is missing one or more of its
        // panel images, throw so the runStage wrapper retries
        // `runAllImages()` (which only regenerates the missing panels,
        // skipping ones already present). This guarantees the video-shots
        // stage never starts on a half-baked shot set. After MAX_RETRIES
        // (3) attempts the user sees a fatal error with a retry button —
        // far better UX than silently producing broken videos.
        if (withImages < totalShots) {
            const missing = totalShots - withImages;
            setStatus('images', 'running', `${withImages}/${totalShots} shots have ${label} — retrying ${missing} incomplete shot${missing === 1 ? '' : 's'}…`);
            throw new Error(
                `${missing} of ${totalShots} shots still have missing ${label}. ` +
                `Retrying the panel-image generation before moving to video.`
            );
        }

        setStatus('images', 'done', `${withImages}/${totalShots} shots have ${label}`);
        if (withImages === 0) throw new Error(`No ${label} were generated.`);
    };

    const stageVideos = async () => {
        if (!storyboardRef.current?.runAllVideos) {
            throw new Error('Storyboard component not mounted.');
        }
        await storyboardRef.current.runAllVideos();
        const scenes = storyboardRef.current.getScenes?.() || projectRef.current.scenes;
        updateProject({ scenes });
        await persist('videos');
        const totalShots = scenes.reduce((n, s) => n + (s.shots?.length || 0), 0);
        const withVideo = scenes.reduce((n, s) => n + (s.shots || []).filter(sh => sh.video).length, 0);
        setStatus('videos', 'done', `${withVideo}/${totalShots} shots have video`);
        if (withVideo === 0) throw new Error('No videos were generated.');
    };

    const stageStudio = async () => {
        const p = projectRef.current;
        if (!createTimelineFromScenes) throw new Error('createTimelineFromScenes not provided.');
        const timeline = await createTimelineFromScenes(p.scenes || [], p.timeline, p.branding);
        updateProject({ timeline });
        await persist('timeline');
        const clipCount = timeline.clips?.length || 0;
        setStatus('studio', 'done', `Timeline ready with ${clipCount} clips`);
    };

    const stageRender = async () => {
        const p = projectRef.current;
        const userEmail = sessionStorage.getItem('userEmail');
        if (!userEmail) throw new Error('User session missing.');

        // Ensure project is saved before render so backend can fetch it.
        try { await saveProjectToBackend(p, userEmail); } catch (e) { /* non-fatal */ }

        const startResult = await startCloudRender(p, userEmail);
        if (!startResult?.success || !startResult.jobId) {
            throw new Error(startResult?.error || 'Failed to start render');
        }
        const result = await pollRenderUntilComplete(startResult.jobId, userEmail, (status) => {
            // pollRenderUntilComplete passes the raw status object — surface
            // its phase to the user.
            const phase = (typeof status === 'string') ? status : (status?.status || status?.message);
            if (phase) setStageMessage(prev => ({ ...prev, render: `Rendering — ${phase}…` }));
        }, 600);
        // backendService returns { success: true, url } on completion (NOT videoUrl).
        const videoUrl = result?.url || result?.videoUrl || result?.video_url;
        if (!result?.success || !videoUrl) {
            throw new Error(result?.error || 'Render finished but no video URL returned');
        }
        updateProject({ renderedVideoUrl: videoUrl });
        // Persist the rendered video as a project asset so it shows up in the
        // Studio editor's media bin AND survives a project reload (the
        // top-level `renderedVideoUrl` is metadata only — assets is the
        // canonical place where playable media lives in the project).
        attachRenderedVideoToAssets(videoUrl);
        await persist('render');
        setDownloadUrl(videoUrl);
        setStatus('render', 'done', 'Final video is ready');
    };

    // Map stage id → stage runner
    const RUNNERS = {
        storyboard: stageStoryboard,
        preprod:    stagePreProd,
        images:     stageImages,
        videos:     stageVideos,
        studio:     stageStudio,
        render:     stageRender,
    };

    // ------------------------------------------------------------------
    // PIPELINE DRIVER
    // ------------------------------------------------------------------

    const runPipelineFrom = useCallback(async (startIdx) => {
        setIsRunning(true);
        setFatalError(null);
        for (let i = startIdx; i < STAGES.length; i++) {
            const stage = STAGES[i];
            setStageIndex(i);
            setStatus(stage.id, 'running', '');
            const { success, lastError } = await runWithRetry(
                stage.label,
                async () => RUNNERS[stage.id](),
                (attempt) => { setRetry(stage.id, attempt); setStatus(stage.id, 'running', `Attempt ${attempt}/${MAX_RETRIES}`); },
            );
            if (!success) {
                setStatus(stage.id, 'error', lastError?.message || 'Unknown error');
                setFatalError({ stageId: stage.id, stageLabel: stage.label, message: lastError?.message || 'Unknown error', stageIndex: i });
                setIsRunning(false);
                if (onRefreshCredits) { try { await onRefreshCredits(); } catch { /* noop */ } }
                return;
            }
        }
        setIsRunning(false);
        if (onRefreshCredits) { try { await onRefreshCredits(); } catch { /* noop */ } }
    }, [onRefreshCredits]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRetryFromStage = () => {
        if (!fatalError) return;
        // Mark the failing stage as pending again so the UI redraws cleanly.
        setStatus(fatalError.stageId, 'pending', '');
        setRetry(fatalError.stageId, 0);
        runPipelineFrom(fatalError.stageIndex);
    };

    // Auto-start the pipeline once on mount.
    useEffect(() => {
        runPipelineFrom(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------------------------------------------------------------------
    // UI
    // ------------------------------------------------------------------

    const allDone = STAGES.every(s => stageStatus[s.id] === 'done');

    return (
        <div className="h-full overflow-y-auto" data-testid="autopilot-page">
            <div className="max-w-3xl mx-auto py-10 px-4">
                <div className="bg-zinc-950/80 border border-white/10 rounded-2xl p-8 shadow-2xl">
                    <div className="flex items-start gap-3 mb-8">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-violet-300" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-white">Auto-Pilot</h2>
                            <p className="text-xs text-zinc-400 mt-0.5">Generating your video end-to-end. Sit back — this can take 5–15 minutes depending on tier.</p>
                        </div>
                    </div>

                    {/* Stage list — `images` label is dynamic: "Panel images"
                        for panel-based projects (Feb 2026), "Frame images"
                        for legacy start/end-frame projects. */}
                    <ol className="space-y-3 mb-8" data-testid="autopilot-stages">
                        {STAGES.map((stage, i) => {
                            const status = stageStatus[stage.id] || (i === stageIndex && isRunning ? 'running' : 'pending');
                            const message = stageMessage[stage.id];
                            const retryCount = stageRetryCount[stage.id];
                            const stageLabel = (stage.id === 'images' && projectRef.current?.usesPanelImages)
                                ? 'Panel images'
                                : stage.label;
                            return (
                                <li key={stage.id} className={`flex items-start gap-3 p-3 rounded-xl border ${
                                    status === 'done'    ? 'bg-emerald-500/5 border-emerald-500/20' :
                                    status === 'running' ? 'bg-violet-500/10 border-violet-500/30' :
                                    status === 'error'   ? 'bg-rose-500/10 border-rose-500/30' :
                                    'bg-white/[0.02] border-white/10'
                                }`} data-testid={`autopilot-stage-${stage.id}`}>
                                    <div className="mt-0.5 flex-shrink-0">
                                        {status === 'done'    && <CheckCircle className="w-5 h-5 text-emerald-400" />}
                                        {status === 'running' && <Loader2 className="w-5 h-5 text-violet-300 animate-spin" />}
                                        {status === 'error'   && <AlertCircle className="w-5 h-5 text-rose-400" />}
                                        {status === 'pending' && <Circle className="w-5 h-5 text-zinc-600" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-semibold text-white">{stageLabel}</div>
                                            {retryCount > 1 && status !== 'done' && (
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300">
                                                    retry {retryCount}/{MAX_RETRIES}
                                                </span>
                                            )}
                                        </div>
                                        {message && (
                                            <div className={`text-xs mt-0.5 ${status === 'error' ? 'text-rose-300' : 'text-zinc-400'}`} data-testid={`autopilot-stage-${stage.id}-msg`}>
                                                {message}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>

                    {/* Failure / retry */}
                    {fatalError && !isRunning && (
                        <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/30" data-testid="autopilot-fatal">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-rose-200">"{fatalError.stageLabel}" failed after {MAX_RETRIES} attempts</div>
                                    <div className="text-xs text-rose-300/80 mt-1 break-words">{fatalError.message}</div>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={handleRetryFromStage}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold"
                                    data-testid="autopilot-retry-btn"
                                >
                                    <RotateCw className="w-4 h-4" />
                                    Retry this step
                                </button>
                                <button
                                    onClick={onBack}
                                    className="px-3 py-2 rounded-lg border border-white/15 text-zinc-300 text-sm hover:bg-white/5"
                                >
                                    Back
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Success */}
                    {allDone && downloadUrl && (
                        <div className="p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="autopilot-done">
                            <div className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-emerald-200">Your video is ready</div>
                                    <div className="text-xs text-emerald-300/80 mt-1 flex items-center gap-1.5" data-testid="autopilot-saved-msg">
                                        <Save className="w-3 h-3" />
                                        Rendered video has been saved to your project.
                                    </div>
                                </div>
                            </div>

                            {/* Inline player */}
                            {showPlayer && (
                                <div className="mt-4 rounded-lg overflow-hidden bg-black border border-white/10" data-testid="autopilot-player">
                                    <video
                                        src={downloadUrl}
                                        controls
                                        autoPlay
                                        className="w-full max-h-[60vh] block bg-black"
                                        data-testid="autopilot-video-element"
                                    />
                                </div>
                            )}

                            {/* Primary actions */}
                            <div className="flex flex-wrap gap-2 mt-4">
                                <button
                                    onClick={() => setShowPlayer(s => !s)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold"
                                    data-testid="autopilot-view-btn"
                                >
                                    <Play className="w-4 h-4" />
                                    {showPlayer ? 'Hide player' : 'View video'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-zinc-200 text-sm font-semibold hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="autopilot-download-btn"
                                >
                                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                    {downloading ? 'Downloading…' : 'Download'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveVideoToProject}
                                    disabled={savingVideoState === 'saving'}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                        savingVideoState === 'saved'
                                            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200'
                                            : savingVideoState === 'error'
                                                ? 'border-rose-500/50 bg-rose-500/10 text-rose-200'
                                                : 'border-white/15 text-zinc-200 hover:bg-white/5'
                                    }`}
                                    data-testid="autopilot-save-video-btn"
                                    title="Add this rendered video to the project's media library"
                                >
                                    {savingVideoState === 'saving'
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : savingVideoState === 'saved'
                                            ? <CheckCircle className="w-4 h-4" />
                                            : <Save className="w-4 h-4" />}
                                    {savingVideoState === 'saving' ? 'Saving…'
                                        : savingVideoState === 'saved' ? 'Saved to project'
                                        : savingVideoState === 'error' ? 'Save failed — retry'
                                        : 'Save video'}
                                </button>
                                {navigator.share && (
                                    <button
                                        onClick={() => navigator.share({ url: downloadUrl, title: project?.name || 'My video' }).catch(() => {})}
                                        className="px-4 py-2 rounded-lg border border-white/15 text-zinc-200 text-sm hover:bg-white/5"
                                        data-testid="autopilot-share-btn"
                                    >
                                        Share
                                    </button>
                                )}
                            </div>

                            {/* Secondary actions — go back to dashboards */}
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10">
                                {onBackToDashboard && (
                                    <button
                                        onClick={onBackToDashboard}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 text-zinc-300 text-xs hover:bg-white/5"
                                        data-testid="autopilot-ad-creator-dashboard-btn"
                                    >
                                        <LayoutGrid className="w-3.5 h-3.5" />
                                        Back to Ad Creator dashboard
                                    </button>
                                )}
                                <a
                                    href="https://app.loopai.in"
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 text-zinc-300 text-xs hover:bg-white/5"
                                    data-testid="autopilot-loopai-dashboard-btn"
                                >
                                    <Home className="w-3.5 h-3.5" />
                                    Back to LoopAI dashboard
                                </a>
                            </div>
                        </div>
                    )}

                    {!fatalError && !allDone && (
                        <button
                            onClick={onBack}
                            disabled={isRunning}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-zinc-400 text-xs hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Tier Selection
                        </button>
                    )}
                </div>
            </div>

            {/* Hidden Storyboard — used as the engine for batch frame-image
                and video generation via its imperative ref. */}
            <div style={{ position: 'fixed', left: '-99999px', top: '-99999px', width: '1px', height: '1px', overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true" data-testid="autopilot-hidden-storyboard">
                <Storyboard
                    ref={storyboardRef}
                    silent={true}
                    projectData={projectRef.current}
                    onSubmit={() => {}}
                    onBack={() => {}}
                    onProjectUpdate={(p) => { projectRef.current = p; if (onProjectUpdate) onProjectUpdate(p); }}
                    onDeductCredits={onDeductCredits}
                    onTrackCreditsUsed={onTrackCreditsUsed}
                    onRefreshCredits={onRefreshCredits}
                    onSaveMedia={onSaveMedia}
                    onAutoSave={onAutoSave}
                />
            </div>
        </div>
    );
};

export default AutoPilot;
